import React from 'react';
import { Users, Heart, Code, Palette, Database, Globe, Coffee, BookOpen } from 'lucide-react';
import BackButton from '../components/BackButton';
import { useLanguage } from '../contexts/LanguageContext';

const OurTeam = () => {
  const { t } = useLanguage();

  const teamMembers = [
    {
      name: 'Ronald Mayer',
      role: 'Project Lead & Full Stack Developer',
      bio: 'Founder of Meet&Eat with a passion for connecting people through food. Ronald oversees the entire project and contributes to both frontend and backend development.',
      icon: Users,
      color: 'orange'
    },
    {
      name: 'Silvia Dorfmann',
      role: 'UX/UI Designer',
      bio: 'Silvia brings her creative vision to Meet&Eat, designing intuitive and beautiful user interfaces that make the app a joy to use.',
      icon: Palette,
      color: 'pink'
    },
    {
      name: 'Thomas Huber',
      role: 'Backend Developer',
      bio: 'Thomas is responsible for the robust backend infrastructure that powers Meet&Eat, ensuring data integrity and system performance.',
      icon: Database,
      color: 'blue'
    },
    {
      name: 'Maria Koller',
      role: 'Frontend Developer',
      bio: 'Maria transforms designs into responsive, interactive user interfaces, focusing on creating seamless user experiences across all devices.',
      icon: Code,
      color: 'purple'
    },
    {
      name: 'Johannes Weber',
      role: 'Marketing & Community Manager',
      bio: 'Johannes builds and nurtures the Meet&Eat community, developing strategies to grow our user base and enhance user engagement.',
      icon: Globe,
      color: 'green'
    },
    {
      name: 'Lisa Berger',
      role: 'Content & Culinary Expert',
      bio: 'With her background in culinary arts, Lisa ensures that Meet&Eat authentically represents diverse food cultures and dining experiences.',
      icon: Coffee,
      color: 'amber'
    }
  ];

  const contributors = [
    'Anna Müller - Beta Testing Lead',
    'Michael Schmidt - Security Consultant',
    'Sophie Wagner - Accessibility Advisor',
    'David Bauer - Mobile Optimization',
    'Julia Hoffmann - Translation Support',
    'Markus Schneider - Database Architecture'
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center space-x-4 mb-8">
        <BackButton />
        <h1 className="text-3xl font-bold text-gray-900">Our Team</h1>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-8 mb-12 text-white">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="md:w-2/3 mb-6 md:mb-0 md:pr-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Meet the People Behind Meet&Eat
            </h2>
            <p className="text-lg opacity-90">
              Our diverse team of passionate individuals is dedicated to creating meaningful connections through shared dining experiences. We combine expertise in technology, design, and culinary knowledge to bring you the best social dining platform.
            </p>
          </div>
          <div className="md:w-1/3 flex justify-center">
            <div className="bg-white/20 backdrop-blur-sm p-6 rounded-full">
              <Users className="h-20 w-20 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Team Members Grid */}
      <div className="grid md:grid-cols-2 gap-8 mb-16">
        {teamMembers.map((member, index) => (
          <div 
            key={index}
            className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow transform hover:-translate-y-1 duration-300"
          >
            <div className="flex items-start space-x-4">
              <div className={`p-3 bg-${member.color}-100 rounded-lg`}>
                <member.icon className={`h-8 w-8 text-${member.color}-500`} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">
                  {member.name}
                </h3>
                <p className={`text-sm font-medium text-${member.color}-600 mb-3`}>
                  {member.role}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  {member.bio}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Contributors Section */}
      <div className="bg-gray-50 rounded-xl p-8 mb-12">
        <div className="flex items-center space-x-3 mb-6">
          <Heart className="h-6 w-6 text-red-500" />
          <h2 className="text-2xl font-bold text-gray-900">Contributors & Supporters</h2>
        </div>
        
        <p className="text-gray-600 mb-6">
          We're grateful to the many individuals who have contributed their time, expertise, and feedback to help make Meet&Eat better.
        </p>
        
        <div className="grid md:grid-cols-2 gap-4">
          {contributors.map((contributor, index) => (
            <div 
              key={index}
              className="bg-white p-4 rounded-lg shadow-sm"
            >
              {contributor}
            </div>
          ))}
        </div>
      </div>

      {/* Our Story Section */}
      <div className="bg-white rounded-xl shadow-md p-8">
        <div className="flex items-center space-x-3 mb-6">
          <BookOpen className="h-6 w-6 text-blue-500" />
          <h2 className="text-2xl font-bold text-gray-900">Our Story</h2>
        </div>
        
        <p className="text-gray-600 mb-4">
          Meet&Eat began in 2023 when founder Ronald Mayer, frustrated with the impersonal nature of existing dining apps, envisioned a platform that would bring people together through authentic culinary experiences.
        </p>
        
        <p className="text-gray-600 mb-4">
          What started as a simple idea quickly grew into a passion project for our founding team. We spent countless hours researching, designing, and developing a platform that would make it easy and enjoyable for people to connect over meals.
        </p>
        
        <p className="text-gray-600">
          Today, we're proud to be building a community of food lovers who share our belief that some of life's most meaningful connections happen around the dinner table. We continue to grow and evolve, guided by our users' feedback and our commitment to fostering genuine human connections.
        </p>
      </div>
    </div>
  );
};

export default OurTeam;